/* ============================================================
   YUVENLIFE — Storefront API Client  (vanilla, zero-dep)
   ------------------------------------------------------------
   Low-level GraphQL-Client für die Shopify Storefront-API.
   Verantwortung NUR: Netzwerk + Resilienz + Normalisierung.
   Kein DOM, kein State. Exponiert window.YuvenStorefront.

   Resilienz lebt hier im request()-Kern:
   · AbortController-Timeout (8s)
   · Retry + exponentielles Backoff + Jitter (max 3) — nur bei
     Netzfehler / Timeout / HTTP 5xx / 429 / GraphQL THROTTLED.
   · NIE Retry bei userErrors (deterministische Business-Fehler).
   · Jede Operation gibt normalisiert zurück:
     { ok, cart|nodes, userErrors:[{code,field,message}], networkError|null }
     → der Aufrufer sieht NIE einen rohen throw.
   ============================================================ */
(function () {
  "use strict";

  var CFG = window.YUVENLIFE_SHOP;
  if (!CFG) { console.error("[yuven-storefront] YUVENLIFE_SHOP config fehlt — Client inaktiv."); return; }

  var ENDPOINT  = "https://" + CFG.shopDomain + "/api/" + CFG.apiVersion + "/graphql.json";
  var TIMEOUT   = 8000;   // ms pro Versuch
  var MAX_TRIES = 3;
  var DBG       = !!(CFG.flags && CFG.flags.debug);

  function log() { if (DBG) console.log.apply(console, ["[yuven-sf]"].concat([].slice.call(arguments))); }
  function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  function backoff(tryNo) { return Math.pow(2, tryNo - 1) * 400 + Math.random() * 250; } // 400/800/1600ms + jitter
  function retryableHttp(s) { return s === 429 || (s >= 500 && s <= 599); }

  /* ── geteiltes Cart-Fragment: liefert checkoutUrl + Totals + Lines aus JEDER Mutation ── */
  var CART_FIELDS =
    "fragment CartFields on Cart {" +
    "  id checkoutUrl totalQuantity" +
    "  cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } totalTaxAmount { amount currencyCode } }" +
    "  lines(first: 50) { nodes {" +
    "    id quantity" +
    "    sellingPlanAllocation { sellingPlan { id name } priceAdjustments { price { amount currencyCode } } }" +
    "    cost { totalAmount { amount currencyCode } }" +
    "    merchandise { ... on ProductVariant {" +
    "      id title availableForSale" +
    "      price { amount currencyCode }" +
    "      product { title featuredImage { url altText } }" +
    "    } }" +
    "  } }" +
    "}";

  /* ── Kern-Request mit Timeout + Retry ──────────────────────────────── */
  function request(query, variables) {
    var tryNo = 0;
    function run() {
      tryNo++;
      var ctrl  = ("AbortController" in window) ? new AbortController() : null;
      var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, TIMEOUT) : null;
      return fetch(ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-Shopify-Storefront-Access-Token": CFG.storefrontToken
        },
        body: JSON.stringify({ query: query, variables: variables || {} }),
        signal: ctrl ? ctrl.signal : undefined
      }).then(function (res) {
        if (timer) clearTimeout(timer);
        if (!res.ok) {
          if (retryableHttp(res.status) && tryNo < MAX_TRIES) {
            var ra = parseFloat(res.headers.get("Retry-After"));
            var wait = !isNaN(ra) ? ra * 1000 : backoff(tryNo);
            log("HTTP " + res.status + " → retry in " + Math.round(wait) + "ms");
            return sleep(wait).then(run);
          }
          return { ok: false, data: null, networkError: { kind: "http", status: res.status } };
        }
        return res.json().then(function (json) {
          if (json.errors && json.errors.length) {
            var throttled = json.errors.some(function (e) { return e.extensions && e.extensions.code === "THROTTLED"; });
            if (throttled && tryNo < MAX_TRIES) { log("THROTTLED → retry"); return sleep(backoff(tryNo)).then(run); }
            return { ok: false, data: json.data || null, networkError: { kind: "graphql", errors: json.errors } };
          }
          return { ok: true, data: json.data, networkError: null };
        });
      }).catch(function (err) {
        if (timer) clearTimeout(timer);
        var aborted = err && err.name === "AbortError";
        if (tryNo < MAX_TRIES) { log((aborted ? "timeout" : "network") + " → retry"); return sleep(backoff(tryNo)).then(run); }
        return { ok: false, data: null, networkError: { kind: aborted ? "timeout" : "network", message: err && err.message } };
      });
    }
    return run();
  }

  /* ── Mutation-Payload → normalisiertes Cart-Result ─────────────────── */
  function extractCart(res, key) {
    if (res.networkError) return { ok: false, cart: null, userErrors: [], networkError: res.networkError };
    var payload = res.data && res.data[key];
    if (!payload) return { ok: false, cart: null, userErrors: [], networkError: { kind: "shape" } };
    var ue = payload.userErrors || [];
    return { ok: ue.length === 0, cart: payload.cart || null, userErrors: ue, networkError: null };
  }

  /* ── Line-Input bauen (sellingPlanId nur wenn Abo aktiviert) ───────── */
  function buildLine(plan, qty) {
    var m = CFG.map[plan];
    if (!m) return null;
    var line = { merchandiseId: m.variantGid, quantity: qty || 1 };
    if (CFG.flags.subscriptionsEnabled && m.type === "subscription" && m.sellingPlanGid) {
      line.sellingPlanId = m.sellingPlanGid;
    }
    return line;
  }

  /* ── Operationen ───────────────────────────────────────────────────── */
  function cartCreate(lines) {
    var q = CART_FIELDS +
      "mutation CartCreate($lines:[CartLineInput!],$country:CountryCode)" +
      " @inContext(country:$country){" +
      "  cartCreate(input:{lines:$lines}){ cart{...CartFields} userErrors{code field message} } }";
    return request(q, { lines: lines, country: CFG.countryCode }).then(function (r) { return extractCart(r, "cartCreate"); });
  }

  function cartLinesAdd(cartId, lines) {
    var q = CART_FIELDS +
      "mutation CartLinesAdd($cartId:ID!,$lines:[CartLineInput!]!){" +
      "  cartLinesAdd(cartId:$cartId,lines:$lines){ cart{...CartFields} userErrors{code field message} } }";
    return request(q, { cartId: cartId, lines: lines }).then(function (r) { return extractCart(r, "cartLinesAdd"); });
  }

  function cartLinesUpdate(cartId, lines) {
    var q = CART_FIELDS +
      "mutation CartLinesUpdate($cartId:ID!,$lines:[CartLineUpdateInput!]!){" +
      "  cartLinesUpdate(cartId:$cartId,lines:$lines){ cart{...CartFields} userErrors{code field message} } }";
    return request(q, { cartId: cartId, lines: lines }).then(function (r) { return extractCart(r, "cartLinesUpdate"); });
  }

  function cartLinesRemove(cartId, lineIds) {
    var q = CART_FIELDS +
      "mutation CartLinesRemove($cartId:ID!,$lineIds:[ID!]!){" +
      "  cartLinesRemove(cartId:$cartId,lineIds:$lineIds){ cart{...CartFields} userErrors{code field message} } }";
    return request(q, { cartId: cartId, lineIds: lineIds }).then(function (r) { return extractCart(r, "cartLinesRemove"); });
  }

  function getCart(cartId) {
    var q = CART_FIELDS + "query GetCart($cartId:ID!){ cart(id:$cartId){...CartFields} }";
    return request(q, { cartId: cartId }).then(function (r) {
      if (r.networkError) return { ok: false, cart: null, networkError: r.networkError };
      return { ok: true, cart: (r.data && r.data.cart) || null, networkError: null };
    });
  }

  function fetchPrices(variantGids) {
    var q = "query Prices($ids:[ID!]!) @inContext(country:" + CFG.countryCode + "){" +
            "  nodes(ids:$ids){ ... on ProductVariant { id availableForSale price{amount currencyCode} } } }";
    return request(q, { ids: variantGids }).then(function (r) {
      if (r.networkError) return { ok: false, nodes: [], networkError: r.networkError };
      return { ok: true, nodes: (r.data && r.data.nodes) || [], networkError: null };
    });
  }

  /* Produktdetails fürs Popover: Beschreibung + Metafelder (custom.details /
     custom.abo_details, Storefront-Access PUBLIC_READ) + Bild + Live-Preis.
     Wolfgang pflegt die Inhalte im Shopify-Produkt-Editor. */
  function fetchProductDetails(variantGids) {
    var q = "query Details($ids:[ID!]!) @inContext(country:" + CFG.countryCode + "){" +
            "  nodes(ids:$ids){ ... on ProductVariant {" +
            "    id availableForSale price{amount currencyCode}" +
            "    product { title descriptionHtml featuredImage{url altText}" +
            "      details: metafield(namespace:\"custom\", key:\"details\"){ value type }" +
            "      aboDetails: metafield(namespace:\"custom\", key:\"abo_details\"){ value type }" +
            "    } } } }";
    return request(q, { ids: variantGids }).then(function (r) {
      if (r.networkError) return { ok: false, nodes: [], networkError: r.networkError };
      return { ok: true, nodes: (r.data && r.data.nodes) || [], networkError: null };
    });
  }

  window.YuvenStorefront = {
    endpoint:       ENDPOINT,
    buildLine:      buildLine,
    cartCreate:     cartCreate,
    cartLinesAdd:   cartLinesAdd,
    cartLinesUpdate:cartLinesUpdate,
    cartLinesRemove:cartLinesRemove,
    getCart:        getCart,
    fetchPrices:    fetchPrices,
    fetchProductDetails: fetchProductDetails
  };
})();
